/**
 * components/charts/Donut — the win / loss / breakeven split as a single ring with a
 * headline in the hole (the dataviz skill's "one number wants a stat, a part-to-whole
 * wants a donut"). The three slices carry a FIXED meaning — a win, a loss, a scratch —
 * so they use the terminal's status poles (profit-green / loss-red / neutral), never a
 * rotating categorical ramp, and every slice is named in the legend with its count and
 * share (identity is never colour-alone). A 2px surface gap separates adjacent arcs.
 */
import { pctPlain } from '../../lib/format';

export interface DonutSlice {
  readonly key: string;
  readonly label: string;
  readonly value: number;
  readonly color: string;
}

interface DonutProps {
  readonly slices: readonly DonutSlice[];
  readonly centerValue: string;
  readonly centerLabel: string;
}

const SIZE = 168;
const R = 68;
const STROKE = 26;
const C = 2 * Math.PI * R;
const GAP = 2; // px surface gap between arcs

export function Donut({ slices, centerValue, centerLabel }: DonutProps): JSX.Element {
  const total = slices.reduce((a, s) => a + s.value, 0);

  if (total === 0) {
    return <div className="state"><span className="muted">NO DATA YET</span></div>;
  }

  let offset = 0;
  const arcs = slices
    .filter((s) => s.value > 0)
    .map((s) => {
      const frac = s.value / total;
      const len = frac * C;
      const arc = { s, dash: Math.max(0, len - GAP), gap: C - Math.max(0, len - GAP), off: -offset };
      offset += len;
      return arc;
    });

  return (
    <div className="donut">
      <svg className="donut__svg" viewBox={`0 0 ${SIZE} ${SIZE}`} role="img" aria-label="Trade outcome split">
        <circle cx={SIZE / 2} cy={SIZE / 2} r={R} fill="none" stroke="var(--viz-grid)" strokeWidth={STROKE} />
        <g transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}>
          {arcs.map((a) => (
            <circle
              key={a.s.key}
              cx={SIZE / 2} cy={SIZE / 2} r={R} fill="none"
              stroke={a.s.color} strokeWidth={STROKE}
              strokeDasharray={`${a.dash} ${a.gap}`} strokeDashoffset={a.off}
            >
              <title>{`${a.s.label}: ${a.s.value} (${pctPlain(a.s.value / total, 0)})`}</title>
            </circle>
          ))}
        </g>
        <text x={SIZE / 2} y={SIZE / 2 - 2} textAnchor="middle" fontSize={26} fontFamily="var(--font-mono)" fontWeight={700} fill="var(--text-primary)">
          {centerValue}
        </text>
        <text x={SIZE / 2} y={SIZE / 2 + 16} textAnchor="middle" fontSize={11} fill="var(--text-muted)">
          {centerLabel}
        </text>
      </svg>
      <ul className="donut__legend">
        {slices.map((s) => (
          <li key={s.key} className="donut__key">
            <span className="donut__swatch" style={{ background: s.color }} aria-hidden />
            <span className="donut__key-label">{s.label}</span>
            <span className="donut__key-val num">
              {s.value} <span className="muted">{total > 0 ? pctPlain(s.value / total, 0) : '0%'}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
